/**
 * AgentManage 页面顶部的异常预警 Banner
 * 创建时间: 2026-04-30
 *
 * 显示最近 60 分钟内的 trade_anomaly 通知:
 *   - 连续止损 >= 3
 *   - 超时平仓率 > 60%
 *   - 胜率 0% 且 >= 3 笔
 *   - 当日亏损 > 5000
 *   - 负期望(盈亏比 < 1) [H 步]
 *   - 期望值为负 [H 步]
 *
 * UX:
 *   - 最多显示 3 条最新告警, 超过则显示 "+N 更多"
 *   - 每条告警可单独点 X 标记已读
 *   - 整组可点 "全部已读"
 *   - 5 秒自动 polling
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatBJ } from "@/lib/timeUtils";

export function AgentAnomalyBanner() {
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());

  const { data, refetch } = (trpc as any).agentMonitor.getActiveAnomalyAlerts.useQuery(undefined, {
    refetchInterval: 5000,  // 5 秒刷新
    refetchOnWindowFocus: true,
  });

  const markRead = (trpc as any).agentMonitor.markAnomalyAlertRead.useMutation({
    onSuccess: () => refetch(),
  });

  const allAlerts = (data?.alerts || []).filter((a: any) => !dismissedIds.has(a.id));
  if (allAlerts.length === 0) return null;

  const visibleAlerts = allAlerts.slice(0, 3);
  const moreCount = allAlerts.length - visibleAlerts.length;

  const handleDismiss = (id: number) => {
    setDismissedIds(prev => new Set([...prev, id]));
    markRead.mutate({ id });
  };

  const handleDismissAll = () => {
    const allIds = allAlerts.map((a: any) => a.id);
    setDismissedIds(prev => new Set([...prev, ...allIds]));
    for (const id of allIds) {
      markRead.mutate({ id });
    }
  };

  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 mb-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <span className="text-sm font-medium text-red-300">
            盘中异常预警 {allAlerts.length > 1 && `(${allAlerts.length})`}
          </span>
        </div>
        {allAlerts.length > 1 && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 text-xs text-muted-foreground hover:text-foreground"
            onClick={handleDismissAll}
          >
            全部已读
          </Button>
        )}
      </div>

      <div className="space-y-1.5">
        {visibleAlerts.map((alert: any) => (
          <div
            key={alert.id}
            className="flex items-start justify-between gap-2 rounded-md bg-background/40 p-2 text-xs"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-medium text-foreground">{alert.stockName}</span>
                <span className="text-[10px] text-muted-foreground">{alert.stockCode}</span>
                <span className="text-[10px] text-muted-foreground">
                  {formatBJ(alert.createdAt, "time")}
                </span>
              </div>
              <div className="text-muted-foreground leading-relaxed break-words">
                {alert.content}
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 shrink-0"
              onClick={() => handleDismiss(alert.id)}
              title="标记为已读"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
        {moreCount > 0 && (
          <div className="text-[10px] text-muted-foreground/70 text-center pt-1">
            还有 {moreCount} 条告警未显示
          </div>
        )}
      </div>
    </div>
  );
}
